import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Award, ArrowRight, CheckCircle2, TrendingUp } from "lucide-react";

export const CompletionPanel = ({ sessionId, dealState, endReason }) => {
  const navigate = useNavigate();

  const getEndMessage = () => {
    switch (endReason) {
      case "persona_ended_call":
        return { title: "Call Ended", desc: "The stakeholder has ended the conversation.", color: "amber" };
      case "trust_collapse":
        return { title: "Trust Collapsed", desc: "Trust dropped too low to continue the meeting.", color: "red" };
      case "completed":
        return { title: "Simulation Complete!", desc: "You've successfully navigated all phases.", color: "emerald" };
      case "user_ended":
        return { title: "Simulation Ended", desc: "You chose to end the simulation.", color: "slate" };
      case "max_turns_reached":
        return { title: "Time's Up", desc: "You've reached the maximum number of turns.", color: "amber" };
      default:
        return { title: "Conversation Complete", desc: "The simulation has concluded.", color: "primary" };
    }
  };

  const msg = getEndMessage();

  return (
    <div className="flex-1 flex items-center justify-center bg-white" data-testid="completion-panel">
      <div className="max-w-md text-center px-6">
        <div className={`w-20 h-20 rounded-full bg-${msg.color}-50 flex items-center justify-center mx-auto mb-6`}>
          {endReason === "completed" ? (
            <Award className={`w-10 h-10 text-${msg.color}-500`} />
          ) : (
            <CheckCircle2 className={`w-10 h-10 text-${msg.color}-500`} />
          )}
        </div>

        <h2 className="text-2xl font-bold text-slate-900 mb-2">{msg.title}</h2>
        <p className="text-sm text-slate-500 mb-6">{msg.desc}</p>

        {/* Quick stats */}
        <div className="flex items-center justify-center gap-6 mb-8">
          <div className="text-center">
            <p className="text-2xl font-bold text-slate-900">{dealState?.session_clock || 0}</p>
            <p className="text-xs text-slate-400">Turns</p>
          </div>
          <div className="w-px h-10 bg-slate-200" />
          <div className="text-center">
            <p className="text-2xl font-bold text-slate-900">{dealState?.trust_score || 0}</p>
            <p className="text-xs text-slate-400">Trust</p>
          </div>
          <div className="w-px h-10 bg-slate-200" />
          <div className="text-center">
            <p className="text-2xl font-bold text-slate-900">
              {dealState ? Math.round(dealState.deal_outcome * 100) : 0}%
            </p>
            <p className="text-xs text-slate-400">Deal Prob.</p>
          </div>
        </div>

        {dealState?.completed_personas?.length > 0 && (
          <div className="flex items-center justify-center gap-2 mb-6">
            <span className="text-xs text-slate-400">Spoke with:</span>
            {[...(dealState?.completed_personas || []), dealState?.active_persona].map(pid => {
              const name = pid === "jamie_sterling" ? "Jamie" : pid === "evelyn_reed" ? "Evelyn" : pid === "ben_carter" ? "Ben" : pid;
              return (
                <Badge key={pid} variant="outline" className="text-xs bg-slate-50">
                  {name}
                </Badge>
              );
            })}
          </div>
        )}

        <Button
          size="lg"
          className="bg-primary hover:bg-primary/90 font-semibold px-8"
          onClick={() => navigate(`/report/${sessionId}`)}
          data-testid="view-report-btn"
        >
          View Evaluation Report
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>

        <p className="text-xs text-slate-300 mt-4">
          Your AI coach has prepared a detailed performance analysis.
        </p>
      </div>
    </div>
  );
};
